import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Player } from "../types";

// Oosler/Woosler picker — same magnified scroll-wheel UX as the score
// entry so the gesture is identical for the user. Single wheel, three
// rows visible at once, tap any visible item to commit + close. Items
// are in spec order:
//   None  ←  default selection (preserves the carry path on accidental
//             dismiss / outside-tap)
//   Player 1
//   Player 2
//   Player 3
//   Player 4
//
// Centered on the viewport: the trigger is hole-level (fires after the
// first score commits), not anchored to a specific cell, so there's no
// natural anchor rect to reuse from the score picker.

interface Props {
  holeNumber: number;
  players: Player[];
  // Rect of the score cell that fired the picker. The wheel is centred
  // over this rect (clamped to the viewport) so the gesture feels
  // continuous with the score entry that just committed.
  anchorRect: DOMRect;
  onSelect: (playerId: string | null) => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 3;
const WHEEL_WIDTH = 180;
const PADDING_X = 12;
// Taller header slot so the big "Oosler" title fits above the wheel.
const LABEL_HEIGHT = 44;

// Sentinel value for the "None" option. The wheel works in strings so
// playerIds and this token can share a single items array; mapped to
// the `null` selection at commit time.
const NONE_VALUE = "__none__";

interface WheelItem {
  value: string;
  label: string;
}

export default function OoslerSelectionModal({
  holeNumber,
  players,
  anchorRect,
  onSelect,
  onClose,
}: Props) {
  const items: WheelItem[] = [
    { value: NONE_VALUE, label: "None" },
    ...players.map((p, i) => ({
      value: p.id,
      // Name only per spec; fall back to "Player N" for unnamed slots
      // so the wheel never renders a blank row.
      label: p.name || `Player ${i + 1}`,
    })),
  ];

  const [value, setValue] = useState<string>(items[0].value);
  const valueRef = useRef(value);
  valueRef.current = value;

  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  // One frame of "armed: false" so the tap that opened the picker
  // doesn't immediately fire the outside-click commit.
  const armedRef = useRef(false);

  // Initial scroll to the default value + arm the outside-click handler.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) {
      const idx = items.findIndex((it) => it.value === valueRef.current);
      if (idx >= 0) el.scrollTop = idx * ITEM_HEIGHT;
    }
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Debounced "scroll settled" → bubble the centred value up so an
  // outside-click commit picks up whatever the user scrolled to.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(items.length - 1, idx));
        const next = items[clamped]?.value;
        if (next !== undefined && next !== valueRef.current) setValue(next);
      }, 80);
    };
    el.addEventListener("scroll", handle, { passive: true });
    return () => {
      el.removeEventListener("scroll", handle);
      if (timer) clearTimeout(timer);
    };
  }, [items]);

  const commitAndClose = useCallback(() => {
    onSelect(valueRef.current === NONE_VALUE ? null : valueRef.current);
    onClose();
  }, [onSelect, onClose]);

  // Tap any visible item → commit immediately. Matches the score-entry
  // picker so the same finger habit applies.
  const onItemTap = useCallback(
    (next: string) => {
      valueRef.current = next;
      setValue(next);
      onSelect(next === NONE_VALUE ? null : next);
      onClose();
    },
    [onSelect, onClose],
  );

  // Outside click / Escape / Enter → commit current scrolled value.
  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      commitAndClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "Enter") commitAndClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [commitAndClose, onClose]);

  // Position the picker centred on the score cell that fired it,
  // clamped inside the viewport so it stays fully on-screen near edges.
  // Same math as ScoreScrollPicker so the gesture feels continuous.
  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;
  const pickerW = WHEEL_WIDTH + PADDING_X * 2;
  const pickerH = wheelH + LABEL_HEIGHT + PADDING_X * 2;
  const cellCenterX = anchorRect.left + anchorRect.width / 2;
  const cellCenterY = anchorRect.top + anchorRect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cellCenterX - pickerW / 2;
  let top = cellCenterY - pickerH / 2;
  if (left < 4) left = 4;
  if (left + pickerW > vw - 4) left = vw - 4 - pickerW;
  if (top < 4) top = 4;
  if (top + pickerH > vh - 4) top = vh - 4 - pickerH;

  return (
    <div className="cg-score-pick-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-score-pick"
        role="dialog"
        aria-label={`Oosler selection for hole ${holeNumber}`}
        style={{ left, top, width: pickerW, height: pickerH }}
      >
        <div className="cg-score-pick-cols">
          <div className="cg-score-pick-col" style={{ width: WHEEL_WIDTH }}>
            <div className="cg-pick-step cg-pick-step-oosler">Oosler</div>
            <div
              className="cg-score-pick-wheel"
              style={{ height: wheelH, width: WHEEL_WIDTH }}
            >
              <div
                className="cg-score-pick-indicator"
                style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }}
              />
              <div
                ref={scrollerRef}
                className="cg-score-pick-scroller"
                style={{ height: wheelH }}
              >
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
                {items.map((it) => (
                  <div
                    key={it.value}
                    className={`cg-score-pick-item${
                      it.value === value ? " cg-score-pick-item-on" : ""
                    }`}
                    style={{ height: ITEM_HEIGHT }}
                    onClick={() => onItemTap(it.value)}
                  >
                    {it.label}
                  </div>
                ))}
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
